'use client'

import { useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { toast } from 'sonner'
import { apiFetch } from '@/lib/api'
import { useAuth } from '@/components/AuthProvider'
import { hardNavigate } from '@/lib/hard-nav'

const CONFIRM_WORD = 'LÖSCHEN'

/**
 * Self-service account deletion confirmation. Required for App Store
 * Guideline 5.1.1(v). Type-to-confirm (irreversible), calls
 * POST /api/auth/delete-account, then signs out and hard-navigates to /login.
 */
export function DeleteAccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { signOut } = useAuth()
  const [confirmText, setConfirmText] = useState('')
  const [busy, setBusy] = useState(false)

  if (!open) return null

  const canDelete = confirmText.trim().toUpperCase() === CONFIRM_WORD

  const handleDelete = async () => {
    if (!canDelete) return
    setBusy(true)
    try {
      const res = await apiFetch('/api/auth/delete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        throw new Error((d as { error?: string })?.error || `Löschung fehlgeschlagen (${res.status})`)
      }
      // Clear the session locally, then hard-navigate away so no stale authed
      // view renders against a now-deleted identity.
      try { await signOut() } catch { /* ignore — we're leaving anyway */ }
      void hardNavigate('/login')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Löschung fehlgeschlagen')
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={() => !busy && onClose()}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-red-50 p-2.5 flex-none">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#001c4d]">Konto endgültig löschen</h3>
              <p className="text-sm text-gray-600 mt-1">
                Diese Aktion kann nicht rückgängig gemacht werden. Ihr Zugang wird gelöscht und Sie
                werden abgemeldet. Ihre persönlichen Daten (Name, E-Mail, Telefon) werden entfernt.
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="confirm-delete" className="block text-sm font-medium mb-1">
              Zum Bestätigen <strong>{CONFIRM_WORD}</strong> eingeben
            </label>
            <Input
              id="confirm-delete"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_WORD}
              autoComplete="off"
              disabled={busy}
            />
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Abbrechen
            </Button>
            <Button
              onClick={handleDelete}
              disabled={!canDelete || busy}
              className="bg-red-600 hover:bg-red-700"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Konto löschen
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
